import { BubbleMenu } from '@tiptap/extension-bubble-menu';
import Paragraph from '@tiptap/extension-paragraph';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';

import { TOOLBAR_ACTIONS, TOOLBAR_BUTTON_LABELS, type ToolbarAction } from './constants.tsx';
import s from './RichTextArea.module.scss';

interface RichTextAreaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  mandatory?: boolean;
  disabled?: boolean;
  maxLength?: number;
}

const isCoarsePointer = () => window.matchMedia('(pointer: coarse)').matches;

const getMarkdown = (storage: any): string => (storage.markdown as MarkdownStorage).getMarkdown();

// The default markdown serializer drops empty paragraphs, so blank lines the user
// inserts in the editor are lost on save. Emit a non-breaking space for empty
// paragraphs so the blank line round-trips through markdown and renders in detail views.
const MarkdownParagraph = Paragraph.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          if (node.childCount === 0) {
            state.write('\u00A0');
          } else {
            state.renderInline(node);
          }
          state.closeBlock(node);
        },
      },
    };
  },
});

export const RichTextArea = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  mandatory,
  disabled,
  maxLength,
}: RichTextAreaProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [, forceUpdate] = useState(0); // incremented on every editor transaction to keep toolbar active-state in sync
  // Lazy-initialized stable element for the bubble menu portal
  const [bubbleMenuElement] = useState<HTMLDivElement>(() => document.createElement('div'));
  // TipTap fires onUpdate during initialization; skip it until onCreate completes
  const isReadyRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, paragraph: false }),
      MarkdownParagraph,
      Markdown.configure({ html: false, tightLists: true }),
      BubbleMenu.configure({
        element: bubbleMenuElement,
        shouldShow: ({ editor: e }) => !isCoarsePointer() && !e.state.selection.empty,
      }),
    ],
    content: value,
    editable: !disabled,
    onCreate: () => {
      isReadyRef.current = true;
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onTransaction: () => forceUpdate((n) => n + 1),
    onUpdate: ({ editor: e }) => {
      if (!isReadyRef.current) return;
      const md = getMarkdown(e.storage);
      if (maxLength && md.length > maxLength) return;
      onChange(md);
    },
  });

  // Sync external value changes (e.g. form reset or edit mode load)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const current = getMarkdown(editor.storage);
    if (current !== value) {
      editor.commands.setContent(value ?? '');
    }
  }, [value, editor]);

  // Sync disabled state
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const runAction = (action: ToolbarAction) => {
    if (!editor) return;
    switch (action.type) {
      case 'bold':
        editor.chain().focus().toggleBold().run();
        break;
      case 'italic':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'bulletList':
        editor.chain().focus().toggleBulletList().run();
        break;
      case 'orderedList':
        editor.chain().focus().toggleOrderedList().run();
        break;
      case 'heading':
        editor.chain().focus().toggleHeading({ level: action.level }).run();
        break;
    }
  };

  const isActive = (action: ToolbarAction): boolean => {
    if (!editor) return false;
    if (action.type === 'heading') return editor.isActive('heading', { level: action.level });
    return editor.isActive(action.type);
  };

  const charCount = editor ? getMarkdown(editor.storage).length : 0;

  const getLabelKey = (action: ToolbarAction): string =>
    action.type === 'heading' ? `heading${action.level}` : action.type;

  const renderToolbarButton = (action: ToolbarAction, key: string) => {
    const active = isActive(action);

    return (
      <button
        key={key}
        type="button"
        className={clsx(s.toolbarBtn, active && s.active)}
        onMouseDown={(e) => e.preventDefault()} // prevent blur on desktop
        onClick={() => runAction(action)}
        aria-label={action.type}
      >
        {TOOLBAR_BUTTON_LABELS[getLabelKey(action)]}
      </button>
    );
  };

  const toolbar = (
    <div className={s.toolbar}>
      {TOOLBAR_ACTIONS.map((action, i) => renderToolbarButton(action, `${action.type}-${i}`))}
    </div>
  );

  return (
    <div className={clsx(s.root, disabled && s.disabled)}>
      <div className={s.labelRow}>
        <span className={s.label}>{label}</span>
        {mandatory && <span className={s.mandatory}>*</span>}
      </div>

      <div
        className={clsx(s.editorWrapper, error && s.error, isFocused && s.focused)}
        onClick={() => {
          if (editor && !editor.isFocused) {
            editor.commands.focus();
          }
        }}
      >
        <div className={clsx(s.fixedToolbar, s.fixedToolbarVisible)}>{toolbar}</div>

        <EditorContent editor={editor} className={s.editorContent} data-placeholder={placeholder} />

        {maxLength && (
          <div className={s.charCount}>
            {charCount}/{maxLength}
          </div>
        )}
      </div>

      {/* Bubble menu portal — BubbleMenu extension controls DOM placement & positioning */}
      {createPortal(<div className={s.bubbleMenu}>{toolbar}</div>, bubbleMenuElement)}

      {error && <p className={s.errorText}>{error}</p>}
    </div>
  );
};
